import React, { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';

// Builds an Airbnb-style rounded "price pill" marker as an inline SVG so it can
// be used directly as a google.maps.Marker icon (no mapId / AdvancedMarker needed).
// Shows the monthly price on top and the vacant-space count underneath, with a
// small tail whose tip is anchored to the property coordinate.
const buildPricePin = (priceLabel, spacesLabel, highlighted = false) => {
  const bg = highlighted ? '#2ec795' : '#1d1d1b';
  const iconGap = 6; // space between grid glyph and the spaces text
  const iconW = 9;
  // Rough text-width estimate keeps the pill snug around whichever line is longer.
  const spacesRowW = spacesLabel.length * 6.4 + iconW + iconGap;
  const w = Math.round(Math.max(priceLabel.length * 8.2, spacesRowW, 52)) + 28;
  const h = 46;
  const tail = 8;
  const totalH = h + tail;
  const cx = w / 2;
  // Center the [grid icon][gap][text] group on the pill's second line.
  const spacesTextW = spacesLabel.length * 6.4;
  const groupW = iconW + iconGap + spacesTextW;
  const iconX = cx - groupW / 2;
  const iconY = 30; // top of the 9x9 glyph
  const textX = iconX + iconW + iconGap;
  // 3x3 grid of tiny squares, mimicking the "spaces" glyph on the cards.
  const cell = 2;
  let grid = '';
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      grid += `<rect x="${(iconX + c * 3.5).toFixed(1)}" y="${(iconY + r * 3.5).toFixed(1)}" width="${cell}" height="${cell}" rx="0.4" fill="#ffffff" opacity="0.9"/>`;
    }
  }
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${totalH}" viewBox="0 0 ${w} ${totalH}">
      <g filter="drop-shadow(0 2px 4px rgba(0,0,0,0.35))">
        <rect x="1.5" y="1.5" width="${w - 3}" height="${h}" rx="13" fill="${bg}" stroke="#ffffff" stroke-width="2"/>
        <path d="M${cx - 7} ${h} L${cx} ${h + tail} L${cx + 7} ${h} Z" fill="${bg}" stroke="#ffffff" stroke-width="0"/>
        <text x="${cx}" y="20" text-anchor="middle" fill="#ffffff" font-family="Montserrat, Arial, sans-serif" font-size="15" font-weight="700">${priceLabel}</text>
        ${grid}
        <text x="${textX}" y="38" text-anchor="start" fill="#ffffff" font-family="Montserrat, Arial, sans-serif" font-size="11" font-weight="500" opacity="0.9">${spacesLabel}</text>
      </g>
    </svg>`;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    size: { w, h: totalH },
  };
};

const GoogleMap = ({ properties, onPropertyClick }) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [infoWindows, setInfoWindows] = useState([]);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    // Define error handler BEFORE attempting to load
    window.gm_authFailure = () => {
      console.log('Google Maps billing error suppressed');
      setShowError(true);
    };
//AIzaSyCizqXIi7RFx0cw4bhgUZyCdCFanr9GgEM

    const initializeMap = async () => {
      const loader = new Loader({
        apiKey: 'AIzaSyAKOXGZDuyS_kQiBnun0rgCbgtKwpK8DlM',
        version: 'quarterly',
        libraries: ['places'],
        region: 'GB',
        language: 'en'
      });

      try {
        const google = await loader.load();

        // Initialize map with same center as Laravel (Sheffield, UK area)
        const mapInstance = new google.maps.Map(mapRef.current, {
          center: { lat: 53.381128999999990000, lng: -1.470085000000040000 },
          zoom: 15,
          mapTypeId: google.maps.MapTypeId.ROADMAP,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        });

        setMap(mapInstance);
        console.log('✅ [GOOGLE MAPS] Map initialized successfully');
      } catch (error) {
        console.error('❌ [GOOGLE MAPS] Failed to initialize map:', error);
        setShowError(true);
      }
    };

    if (mapRef.current && !showError) {
      initializeMap();
    }
  }, []);

  useEffect(() => {
    if (!map || !properties || properties.length === 0) {
      return;
    }

    // Clear existing markers and info windows
    markers.forEach(marker => {
      marker.setMap(null);
    });
    infoWindows.forEach(infoWindow => {
      infoWindow.close();
    });

    const newMarkers = [];
    const newInfoWindows = [];

    properties.forEach(property => {
      // Skip properties without valid coordinates
      // Check for coordinates in nested object or direct lat/lng fields
      const coordinates = property.coordinates || property;
      const latValue = coordinates.lat || property.geo_latitude;
      const lngValue = coordinates.lng || property.geo_longitude;
      
      if (!latValue || !lngValue) {
        console.warn(`⚠️ [GOOGLE MAPS] Property ${property.id} missing coordinates`);
        return;
      }

      const lat = parseFloat(latValue);
      const lng = parseFloat(lngValue);

      // Validate coordinates
      if (isNaN(lat) || isNaN(lng)) {
        console.warn(`⚠️ [GOOGLE MAPS] Invalid coordinates for property ${property.id}`);
        return;
      }

      const position = { lat, lng };

      // Build the "price pill" marker from the same data the cards use.
      const pinPrice =
        property.price_from ||
        property.price;
      const pinPriceLabel = pinPrice ? `£${pinPrice}` : '£--';
      const pinSpaceCount =
        property.vacantRooms?.length ??
        property.available_spaces ??
        0;
      const pinSpacesLabel = `${pinSpaceCount} ${pinSpaceCount === 1 ? 'space' : 'spaces'}`;
      const pin = buildPricePin(pinPriceLabel, pinSpacesLabel);

      // Create marker
      const marker = new window.google.maps.Marker({
        position: position,
        map: map,
        title: property.name || property.address || `Property ${property.id}`,
        icon: {
          url: pin.url,
          scaledSize: new window.google.maps.Size(pin.size.w, pin.size.h),
          // Anchor at the tip of the tail so the pill floats above the point.
          anchor: new window.google.maps.Point(pin.size.w / 2, pin.size.h)
        }
      });

      // Create info window content matching current data structure
      const priceText = property.price || property.price_from || property.cheapestVacantRoomPrice;
      const priceDisplay = priceText ? `£${priceText}` : 'Price on request';
      const propertyName = property.name || property.address || `Property ${property.id}`;
      const propertyAddress = property.address || `${property.town || ''} ${property.postcode || ''}`.trim();
      const propertyImage = property.main_photo || '/images/default-property.jpg';
      
      const spacesAvailable =
        property.vacantRooms?.length ?? property.available_spaces ?? 0;

      const infoWindowContent = `
        <div style="width: 260px; font-family: Montserrat, Arial, sans-serif; overflow: hidden;">
          <div style="width: 100%; height: 150px; border-radius: 12px; overflow: hidden; background: #f7f7f7;">
            <img src="${propertyImage}" alt="${propertyName}"
                 style="width: 100%; height: 100%; object-fit: cover; display: block;"
                 onerror="this.onerror=null;this.src='/images/default-property.jpg';" />
          </div>
          <div style="padding: 10px 2px 2px;">
            <div style="display: flex; align-items: baseline; gap: 6px;">
              <span style="color: #1d1d1b; font-size: 20px; font-weight: 700;">${priceDisplay}</span>
              <span style="color: #1d1d1b; font-size: 13px; opacity: 0.7;">per month</span>
            </div>
            <div style="margin: 6px 0 2px; color: #1d1d1b; opacity: 0.7; font-size: 13px; line-height: 1.35;">${propertyAddress}</div>
            <div style="margin: 6px 0 10px; color: #1d1d1b; font-size: 12px; font-weight: 600;">
              ${spacesAvailable} ${spacesAvailable === 1 ? 'space' : 'spaces'} available
            </div>
            <a href="#" onclick="window.handlePropertyClick && window.handlePropertyClick(${property.id}); return false;"
               style="display: inline-block; padding: 8px 16px; background: #2ec795; color: #ffffff; text-decoration: none; font-weight: 600; font-size: 13px; border-radius: 10px;">More Information</a>
          </div>
        </div>
      `;

      // Create info window
      const infoWindow = new window.google.maps.InfoWindow({
        content: infoWindowContent,
        maxWidth: 500
      });

      // Add click listener to marker
      marker.addListener('click', () => {
        // Close all other info windows
        newInfoWindows.forEach(iw => iw.close());
        
        // Open this info window
        infoWindow.open(map, marker);
      });

      newMarkers.push(marker);
      newInfoWindows.push(infoWindow);
    });

    setMarkers(newMarkers);
    setInfoWindows(newInfoWindows);

    // Set up global property click handler
    window.handlePropertyClick = (propertyId) => {
      if (onPropertyClick) {
        onPropertyClick(propertyId);
      }
    };

    // Adjust map bounds to show all markers
    if (newMarkers.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      newMarkers.forEach(marker => {
        bounds.extend(marker.getPosition());
      });
      
      if (newMarkers.length === 1) {
        // If only one marker, center on it with appropriate zoom
        map.setCenter(newMarkers[0].getPosition());
        map.setZoom(15);
      } else {
        // Fit all markers in view
        map.fitBounds(bounds);
        
        // Ensure minimum zoom level
        const listener = window.google.maps.event.addListener(map, 'idle', () => {
          if (map.getZoom() > 16) map.setZoom(16);
          window.google.maps.event.removeListener(listener);
        });
      }
    }

    console.log(`✅ [GOOGLE MAPS] Created ${newMarkers.length} markers for ${properties.length} properties`);

  }, [map, properties]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      markers.forEach(marker => {
        marker.setMap(null);
      });
      infoWindows.forEach(infoWindow => {
        infoWindow.close();
      });
      if (window.handlePropertyClick) {
        delete window.handlePropertyClick;
      }
    };
  }, []);

  // Show error fallback UI if billing error occurred
  if (showError) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        minHeight: '320px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8f9fa',
        padding: '20px',
        textAlign: 'center'
      }}>
        <i className="fa fa-map-marker-alt" style={{ fontSize: '48px', color: '#6c757d', marginBottom: '16px' }}></i>
        <h3 style={{ color: '#495057', margin: '0 0 8px 0', fontSize: '18px' }}>Map View Unavailable</h3>
        <p style={{ color: '#6c757d', margin: '0', fontSize: '14px' }}>
          The map cannot be displayed at this time.<br/>
          Please use the list view below to browse properties.
        </p>
      </div>
    );
  }

  return (
    <div
      ref={mapRef}
      style={{
        width: '100%',
        height: '100%',
        minHeight: '320px'
      }}
    />
  );
};

export default GoogleMap;
